import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Divider,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { formatMoney, formatPct } from "../../utils/money";

function Row({ label, value, emphasis }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography
        variant={emphasis ? "subtitle1" : "body2"}
        color={emphasis ? "text.primary" : "text.secondary"}
        fontWeight={emphasis ? 700 : 400}
      >
        {label}
      </Typography>
      <Typography
        variant={emphasis ? "h6" : "body2"}
        fontWeight={emphasis ? 700 : 400}
      >
        {value}
      </Typography>
    </Stack>
  );
}

function partition(quote) {
  const lines = quote.lines || [];
  const q = Number(quote.markupPct);
  let exemptCost = 0; // C_e
  let nonExemptCost = 0; // C_n
  let adHocCost = 0;
  let adHocPrice = 0; // P_a
  let setPrice = 0; // catalogue lines with a sell price or a hand-set unit price: fixed like ad-hoc, but not pass-through
  for (const l of lines) {
    const totalCost = Number(l.totalCost) || 0;
    const totalPrice = Number(l.totalPrice) || 0;
    if (l.sellPriceSet || l.unitPriceOverridden) {
      setPrice += totalPrice;
    } else if (l.isAdHoc) {
      adHocCost += totalCost;
      adHocPrice += totalPrice;
    } else if (Number.isFinite(q) && q > 0 && Number(l.markupPct) === 0) {
      exemptCost += totalCost;
    } else {
      nonExemptCost += totalCost;
    }
  }
  // Markup back-solving treats set-price lines like ad-hoc ones: their price doesn't move.
  return { exemptCost, nonExemptCost, adHocCost, adHocPrice: adHocPrice + setPrice };
}

const fmt2 = (n) => (Number.isFinite(n) ? n.toFixed(2) : "");
const fmt4 = (n) => (Number.isFinite(n) ? n.toFixed(4) : "");

function formatWholePounds(value) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "";
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export default function TotalsPanel({ quote, disabled, onMarkupChange }) {
  const { exemptCost, nonExemptCost, adHocCost, adHocPrice } = useMemo(
    () => partition(quote),
    [quote],
  );
  const noMarkupableLines = nonExemptCost === 0;
  const fixedPrice = exemptCost + adHocPrice; // doesn't move with markup
  // The quote markup may go negative to hit a target price, as long as the quote
  // as a whole still covers its cost (fixed-price lines can carry the margin).
  const costFloor = Number(quote.totalCost) || 0;
  const priceAt = (m) => fixedPrice + nonExemptCost * (1 + m / 100);
  const belowCost = (T) => Math.round(T * 100) < Math.round(costFloor * 100);
  const passThroughCost = exemptCost + adHocCost;
  const studentPax = Number(quote.studentPax) || 0;

  const [markupDraft, setMarkupDraft] = useState(quote.markupPct ?? "");
  const [priceDraft, setPriceDraft] = useState(quote.totalPrice ?? "");
  const [perStudentDraft, setPerStudentDraft] = useState(quote.pricePerStudent ?? "");
  const [floorWarning, setFloorWarning] = useState(false);

  useEffect(() => {
    setMarkupDraft(quote.markupPct ?? "");
    setPriceDraft(quote.totalPrice ?? "");
    setPerStudentDraft(quote.pricePerStudent ?? "");
    setFloorWarning(false);
  }, [quote.id, quote.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const propagateFromPrice = (T) => {
    if (!Number.isFinite(T) || noMarkupableLines) return;
    if (belowCost(T)) { setFloorWarning(true); return; }
    setFloorWarning(false);
    setMarkupDraft(fmt4(((T - fixedPrice) / nonExemptCost - 1) * 100));
  };

  const onMarkupTyped = (v) => {
    setMarkupDraft(v);
    const m = Number(v);
    if (!Number.isFinite(m)) { setFloorWarning(false); return; }
    if (noMarkupableLines) return;
    const T = priceAt(m);
    if (belowCost(T)) { setFloorWarning(true); return; }
    setFloorWarning(false);
    setPriceDraft(fmt2(T));
    if (studentPax > 0) setPerStudentDraft(fmt2(T / studentPax));
  };

  const onPriceTyped = (v) => {
    setPriceDraft(v);
    const T = Number(v);
    if (Number.isFinite(T) && studentPax > 0) setPerStudentDraft(fmt2(T / studentPax));
    propagateFromPrice(T);
  };

  const onPerStudentTyped = (v) => {
    setPerStudentDraft(v);
    const ps = Number(v);
    if (!Number.isFinite(ps) || studentPax <= 0) return;
    const T = ps * studentPax;
    setPriceDraft(fmt2(T));
    propagateFromPrice(T);
  };

  useEffect(() => {
    if (disabled) return undefined;
    if (floorWarning) return undefined;
    if (String(markupDraft) === String(quote.markupPct ?? "")) return undefined;
    const t = setTimeout(() => {
      const n = Number(markupDraft);
      if (!Number.isFinite(n) || belowCost(priceAt(n))) return;
      onMarkupChange?.(n);
    }, 500);
    return () => clearTimeout(t);
  }, [markupDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputsDisabled = disabled || noMarkupableLines;
  const perStudentDisabled = inputsDisabled || studentPax <= 0;

  // Live margin / quote-price derived from the user's price draft (predicted).
  const predictedPrice = Number(priceDraft);
  const totalCost = Number(quote.totalCost);
  // A refused price isn't applied, so show the saved figures rather than the typed one.
  const useDraft = !floorWarning && Number.isFinite(predictedPrice) && Number.isFinite(totalCost);
  const displayMargin = useDraft ? predictedPrice - totalCost : Number(quote.totalMargin);
  const displayMarginPct =
    useDraft && predictedPrice > 0 ? (displayMargin / predictedPrice) * 100 : Number(quote.totalMarginPct);
  const displayQuotePrice = useDraft ? predictedPrice : Number(quote.totalPrice);

  const markupTooltip =
    "Markup is added on top of cost: £100 cost at 20% markup sells for £120. " +
    "Margin is the share of the price you keep: £20 of £120 is 16.7%. " +
    "Markup applies to every line except set-price, your-price, markup-exempt and ad-hoc lines.";

  return (
    <Paper sx={{ p: 2, position: { md: "sticky" }, top: 16 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Totals</Typography>
      <Stack spacing={1.5}>
        <Row label="Cost" value={formatMoney(quote.totalCost)} emphasis />
        <Row label="Pass-through cost" value={formatMoney(passThroughCost)} />
        <TextField
          label="Markup %"
          size="small"
          type="number"
          value={markupDraft}
          disabled={disabled}
          onChange={(e) => onMarkupTyped(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title={markupTooltip}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary", cursor: "help" }} />
                </Tooltip>
              </InputAdornment>
            ),
            endAdornment: <InputAdornment position="end">%</InputAdornment>,
          }}
        />
        <TextField
          label="Price / student"
          size="small"
          type="number"
          value={perStudentDraft}
          disabled={perStudentDisabled}
          onChange={(e) => onPerStudentTyped(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
          helperText={studentPax <= 0 ? "Set student pax to enable" : undefined}
        />
        <TextField
          label="Quote price"
          size="small"
          type="number"
          value={priceDraft}
          disabled={inputsDisabled}
          onChange={(e) => onPriceTyped(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
          helperText={noMarkupableLines ? "All lines are exempt or ad-hoc; markup doesn't apply." : undefined}
        />
        <Divider />
        <Row label="Margin" value={formatMoney(displayMargin)} />
        <Tooltip title="Margin ÷ quote price: the share of the price you keep. It's always lower than the markup on the same figures.">
          <span><Row label="Margin %" value={formatPct(displayMarginPct)} /></span>
        </Tooltip>
        <Divider />
        <Row label="Quote price" value={formatWholePounds(displayQuotePrice)} emphasis />
      </Stack>
      {floorWarning && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Quote price is below total cost ({formatMoney(costFloor)}). The lowest you can quote is {formatMoney(costFloor)}.
        </Alert>
      )}
      {quote.hasMissingPrices && (
        <Alert severity="error" sx={{ mt: 2 }}>
          One or more lines have no available pricing — totals exclude them. Resolve before sending.
        </Alert>
      )}
      {quote.marginBelowThreshold && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Margin is below the configured warning threshold.
        </Alert>
      )}
    </Paper>
  );
}
